import { Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap'
import React, { FC, useContext, useEffect, useState } from 'react'
import { FieldValue, FieldValues } from 'react-hook-form'
import { RootState } from '@store/store'
import { useSelector } from 'react-redux'
import { isJobInProgress } from '@editor/store/selectors/job.selector'
import {
  fetchAudioById,
  selectAudioById,
} from '@editor/store/slices/audio.slice'
import { JobModel, JobState, updateJob } from '@editor/store/slices/job.slice'
import {
  EditorContext,
  EditorContextType,
} from '@editor/components/editorContext'
import {
  fetchSsmlById,
  SsmlModel,
  updateSsml,
} from '@editor/store/slices/ssml.slice'
import { ssmlToAudio } from '@editor/store/thunks/job.thunk'
import { toast } from 'react-toastify'
import { unwrapResult } from '@reduxjs/toolkit'
import { useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import CustomAudioPlayer from '@editor/components/misc/customAudioPlayer'
import { ArticleModel } from '@editor/store/slices/article.slice'
import { stringsEqualWithoutWhitespaces } from '../../../../tools/string.tools'

interface ToolBarProps {
  activeTab: string
}

const ToolBar: FC<ToolBarProps> = ({ activeTab }) => {
  const {
    dispatch,
    formMethods,
    ssml,
    job,
    editorRef,
    article,
    setSelectedRange,
  } = useContext(EditorContext) as EditorContextType
  const { jobId } = useParams()
  const [audioSrc, setAudioSrc] = useState('')
  const { handleSubmit, setValue, getValues } = formMethods
  const audioLoading = useSelector((state: RootState) =>
    !!jobId ? isJobInProgress(state)(jobId) : false
  )
  const audio = useSelector((state: RootState) =>
    job?.audio ? selectAudioById(state, job.audio) : null
  )
  const [audioDownloading, setAudioDownloading] = useState<boolean>(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)

  useEffect(() => {
    if (job && !job.audio) setAudioSrc('')
    if (job && job.audio && !audio) dispatch(fetchAudioById(job.audio))
  }, [job])

  useEffect(() => {
    if (audio && audio.audioUrl) {
      setAudioSrc(audio.audioUrl)
    }
  }, [audio])

  useEffect(() => {
    if (audioDownloading && audioBlob && article) {
      serveAudioToUser(audioBlob, article)
    }
    setAudioDownloading(false)
  }, [audioBlob])

  useEffect(() => {
    window.addEventListener('keydown', onKeyDownHandler)
    return () => {
      window.removeEventListener('keydown', onKeyDownHandler)
    }
  }, [])

  const onKeyDownHandler = (event: any) => {
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault()
      onSaveClick(getValues)
    }
  }

  const confirmMutualEdit = (ssmlArea: string, editorArea: string) => {
    let proceed = true

    if (activeTab == 'Editor') {
      if (ssml && !stringsEqualWithoutWhitespaces(ssmlArea, ssml.ssmlContent)) {
        proceed = window.confirm(
          'Obsah karty SSML byl také změněn. Tato akce přepíše SSML podle obsahu Editoru. Přejete si pokračovat?'
        )
      }
    } else if (activeTab == 'SSML') {
      if (
        ssml &&
        !stringsEqualWithoutWhitespaces(editorArea, ssml.ssmlContent)
      ) {
        proceed = window.confirm(
          'Obsah karty Editor byl také změněn. Tato akce přepíše Editor podle obsahu SSML. Přejete si pokračovat?'
        )
      }
    }
    return proceed
  }

  const onSaveClick = (data: FieldValue<FieldValues>) => {
    const { editorArea, ssmlArea } = data

    if (!confirmMutualEdit(ssmlArea, editorArea)) {
      return
    }

    let newValue

    if (activeTab == 'Editor') {
      newValue = editorArea
    } else if (activeTab == 'SSML') {
      newValue = ssmlArea
    }

    if (ssml && ssml.id) {
      const updatedSsml: SsmlModel = {
        ...ssml,
        ssmlContent: newValue,
      }
      dispatch(updateSsml({ id: ssml.id, data: updatedSsml }))
        .then(unwrapResult)
        .then(onUpdateSuccess)
        .catch(onUpdateError)
    }
  }

  const onUpdateSuccess = () => {
    toast.success(`Článek uložen`)
    if (ssml?.id) dispatch(fetchSsmlById(ssml?.id))
  }

  const onUpdateError = (error: any) => {
    toast.error(error)
    console.error('Článek se nepodařilo uložit:', error)
  }

  const onResetClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const editorElement = editorRef.current
    if (ssml && editorElement) {
      editorElement.innerHTML = ssml.ssmlContent
      setValue('editorArea', ssml.ssmlContent)
      setValue('ssmlArea', ssml.ssmlContent)
      setSelectedRange(null)
    }
  }

  const cleanText = (ssml: string): string => {
    return ssml.replace(/&nbsp;/g, ' ')
  }

  const onGenerateAudioClick = (data: FieldValue<FieldValues>) => {
    let ssml
    const { editorArea, ssmlArea } = data

    if (!confirmMutualEdit(ssmlArea, editorArea)) {
      return
    }

    if (activeTab == 'Editor') {
      ssml = editorArea
    } else if (activeTab == 'SSML') {
      ssml = ssmlArea
    }
    toast.info(`Audio ${job?.id} se generuje`)
    setAudioSrc('')
    if (job?.id)
      dispatch(ssmlToAudio({ ssml: cleanText(ssml), jobId: job.id }))
        .then(unwrapResult)
        .then(onGenerateSuccess)
        .catch(onGenerateError)
  }

  const onGenerateSuccess = (job: JobModel) => {
    toast.success(`Audio ${job.id} vytvořeno`)
  }

  const onGenerateError = (error: any) => {
    toast.error(error)
    console.error('Audio se nepodařilo vytvořit:', error)
  }

  const serveAudioToUser = (audioBlob: Blob, article: ArticleModel) => {
    const link = document.createElement('a')
    const url = URL.createObjectURL(audioBlob)
    const articleDomain = new URL(article.url).hostname
      .replace(/^www\./, '')
      .replace(/\.cz/, '')

    link.href = url
    link.download = `article-${articleDomain}-${article.articleId}.wav` // Default name if audioName is not available
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.info('Audio se staženo')
  }

  const onDownloadClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!audioSrc) {
      console.error('No audio source available for download.')
      toast.error('No audio source available for download.')
      return
    }

    try {
      setAudioDownloading(true)
      toast.info('Audio se stahuje')
      const response = await fetch(audioSrc)

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const blob = await response.blob()
      setAudioBlob(blob)
    } catch (error) {
      console.error('Error during fetch:', error)
      toast.error('An error occurred while downloading the audio file.')
    }
  }

  const onPublishedClick = () => {
    if (job) {
      dispatch(
        updateJob({
          id: job.id,
          data: { ...job, state: JobState.PUBLISHED_STATE },
        })
      )
        .then(unwrapResult)
        .then(onPublishSuccess)
        .catch(onPublishError)
    }
  }

  const onPublishSuccess = () => {
    toast.success(`Audio označeno za publikované`)
  }

  const onPublishError = (error: any) => {
    toast.error(error)
    console.error('Audio se nepodařilo označit jako publikované:', error)
  }

  const onErroredClick = () => {
    if (job) {
      dispatch(
        updateJob({
          id: job.id,
          data: { ...job, state: JobState.ACTIONNEEDED_STATE },
        })
      )
        .then(unwrapResult)
        .then(onErroredSuccess)
        .catch(onErroredError)
    }
  }

  const onErroredSuccess = () => {
    toast.success(`Audio označeno za chybné`)
  }

  const onErroredError = (error: any) => {
    toast.error(error)
    console.error('Audio se nepodařilo označit jako chybné:', error)
  }

  const compareDateStrings = (
    date1: string | undefined,
    date2: string | undefined
  ): boolean => {
    return !!date1 && !!date2 && new Date(date1) > new Date(date2)
  }

  return (
    <Card className="p-3">
      <Container>
        <Row>
          <Col>
            <Button
              onClick={handleSubmit(onGenerateAudioClick)}
              variant={
                compareDateStrings(audio?.createdAt, ssml?.updatedAt)
                  ? 'secondary'
                  : 'primary'
              }
              type="submit"
              style={{ minWidth: '150px', minHeight: '40px' }}
              disabled={audioLoading}
              className={
                'd-flex justify-content-center align-items-center fw-bold'
              }
            >
              Generovat audio
            </Button>
          </Col>
        </Row>
        <hr />
        <Row>
          {!!audioSrc && !audioLoading && (
            <>
              <CustomAudioPlayer audioSrc={audioSrc} />
            </>
          )}
          {!audioSrc && !audioLoading && (
            <strong>Audio ještě není vygenerováno</strong>
          )}
          {audioLoading && <strong>Audio se generuje</strong>}
        </Row>
        <hr />
        <Row>
          <Col className="d-flex">
            <Button
              onClick={onResetClick}
              className="me-2 fw-bold"
              variant="outline-danger"
              type="submit"
              data-tooltip-content="Obnoví stav článku do posledního uloženého"
              data-tooltip-id="reset-tooltip"
              data-tooltip-place="top"
            >
              Reset
            </Button>
            <Tooltip id="reset-tooltip" />
            <Button
              onClick={handleSubmit(onSaveClick)}
              variant="outline-primary"
              type="submit"
              className="fw-bold"
            >
              Uložit
            </Button>
          </Col>
          <Col className="d-flex justify-content-end">
            <Button
              disabled={!audio || !audioSrc || audioDownloading}
              onClick={onDownloadClick}
              variant="primary"
              type="submit"
              className="fw-bold me-2 d-flex justify-content-between align-items-center"
            >
              {audioDownloading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  Stahuji...
                </>
              ) : (
                'Stáhnout'
              )}
            </Button>
            {job?.state !== JobState.PUBLISHED_STATE ? (
              <>
                <Button
                  disabled={!audioSrc}
                  onClick={handleSubmit(onPublishedClick)}
                  variant="success"
                  type="submit"
                  className="fw-bold"
                  data-tooltip-content="Označí článek jako publikovaný"
                  data-tooltip-id="publish-tooltip"
                  data-tooltip-place="top"
                >
                  Publikovat
                </Button>
                <Tooltip id="publish-tooltip" />
              </>
            ) : (
              <>
                <Button
                  disabled={!audioSrc}
                  onClick={handleSubmit(onErroredClick)}
                  variant="warning"
                  type="submit"
                  className="fw-bold"
                  data-tooltip-content="Označí článek jako chybový"
                  data-tooltip-id="error-tooltip"
                  data-tooltip-place="top"
                >
                  Chyba
                </Button>
                <Tooltip id="error-tooltip" />
              </>
            )}
          </Col>
        </Row>
      </Container>
    </Card>
  )
}
export default ToolBar
